import client from '@/api/client'
import dataForViewQuery from '@/api/gql/raffle/query/data-for-view.gql'
import raffleEntryQuery from '@/api/gql/raffle/query/raffle-entry.gql'
import createOrUpdateRaffleEntryMutation from '@/api/gql/raffle/mutation/create-or-update-raffle-entry.gql'
import { camelizeKeys, deleteProperties, isEmpty } from '@/helpers/object'

const formatAddress = address => {
  const addressFormatted = camelizeKeys(address)
  deleteProperties(addressFormatted, ['countryData'])
  return addressFormatted
}

export const RAFLLE_STATES = {
  dataEntry: 'data_entry',
  entered: 'entered',
  selected: 'selected',
  rejected: 'rejected',
}

export default {
  namespaced: true,
  state: {
    id: 0,
    state: RAFLLE_STATES.data_entry,
    email: '',
    variant: {},
    shippingAddress: {},
    billingAddress: {},
    originKeyResponse: {},
    paymentMethodsResponse: {},
    lastAdyenPaymentResponse: {},
    errors: null,
    isLoaded: false,
  },
  mutations: {
    setRaffle(state, raffle) {
      for (const key in raffle) {
        state[key] = raffle[key]
      }
    },
    setIsLoaded(state, isLoaded) {
      state.isLoaded = isLoaded
    },
    clearErrors(state) {
      state.errors = null
    },
  },
  actions: {
    async fetchRaffleEntryAndViewDataByVariantId({ dispatch }, { variantId, countryIso }) {
      // fetching by variant id is only supported via mutation
      await dispatch('updateRaffle', { variantId })
      const { data } = await client.query({ query: dataForViewQuery })
      await dispatch('handlResponse', { ...data, countryIso })
    },

    async fetchRaffleEntryAndViewDataByRaffleId({ dispatch, rootState }, { raffleId, countryIso }) {
      const { data } = await client.query({
        query: raffleEntryQuery,
        variables: {
          raffleId,
          originDomain: rootState.page.hostname,
        },
      })
      await dispatch('handlResponse', { ...data, countryIso })
      dispatch('validatePaymentConfiguration')
    },

    async updateRaffle(
      { dispatch, rootState },
      {
        id,
        variantId,
        email = '',
        shippingAddress = {},
        billingAddress = {},
        makePaymentRequest = {},
      }
    ) {
      const shippingAddressFormatted = formatAddress(shippingAddress)
      const billingAddressFormatted = formatAddress(billingAddress)
      const variables = {
        attributes: {
          id,
          email,
          variantId,
          shippingAddress: shippingAddressFormatted,
          billingAddress: billingAddressFormatted,
          makePaymentRequest: {
            data: makePaymentRequest,
          },
        },
        originDomain: rootState.page.hostname,
      }
      const { data } = await client.mutate({
        mutation: createOrUpdateRaffleEntryMutation,
        variables,
      })
      await dispatch('handlResponse', data.createOrUpdateRaffleEntry)
      dispatch('validatePaymentConfiguration')
    },

    validatePaymentConfiguration({ getters }) {
      if (!getters.isPaymentConfigurationLoaded) {
        throw new Error('Raffle payment configuration could not be loaded.')
      }
    },

    async handlResponse(
      { commit, dispatch },
      { raffleEntry, currentOrder, territories, paths, countryIso = '' }
    ) {
      commit('setRaffle', raffleEntry)
      commit('setIsLoaded', true)
      if (currentOrder) dispatch('order/handleFetchData', { currentOrder }, { root: true })
      if (territories) dispatch('geo/handleFetchData', { territories }, { root: true })
      if (paths) dispatch('paths/handleFetchData', { paths }, { root: true })
      if (countryIso) await dispatch('geo/fetchStates', countryIso, { root: true })
    },
  },
  getters: {
    // raffle is considered as expired also when raffleExpiresAt is not present
    hasRaffleExpired: state => Date.parse(state.variant?.raffleExpiresAt || 0) - Date.now() < 0,
    isValidRaffle: (state, getters) =>
      state.id && state.variant?.isRaffle && !getters.hasRaffleExpired,
    isRaffleEntered: state =>
      [RAFLLE_STATES.entered, RAFLLE_STATES.selected, RAFLLE_STATES.rejected].includes(state.state),
    currentOriginKey: (state, getters, rootState) =>
      state.originKeyResponse?.originKeys?.[rootState.page.hostname] || '',
    isPaymentConfigurationLoaded: (state, getters) =>
      !!getters.currentOriginKey && !isEmpty(state.paymentMethodsResponse),
  },
}
